'use client';

import {
  Button,
  createListCollection,
  Dialog,
  Flex,
  IconButton,
  Input,
  Portal,
  SimpleGrid,
  Spinner,
  Tabs,
  Text,
} from '@chakra-ui/react';
import EntityPicker from '@components/common/EntityPicker';
import SelectField from '@features/spend/SelectField';
import { ITEM_UNITS, MERCHANDISE_CATEGORIES } from 'helpers/fakeData';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { LuFileUp, LuPenLine, LuPlus, LuSparkles, LuX } from 'react-icons/lu';

export interface MerchandiseDraftRow {
  id: string;
  name: string;
  quantity: string;
  unit: string;
  category: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  initialRows?: MerchandiseDraftRow[];
  title?: string;
}

type ExtractionState = 'idle' | 'parsing' | 'done';

const unitCollection = createListCollection({
  items: ITEM_UNITS.map((unit) => ({ value: unit as string, label: unit as string })),
});

const categoryOptions = MERCHANDISE_CATEGORIES.map((category) => ({
  id: category as string,
  label: category as string,
}));

const inputProps = {
  borderColor: 'border.light',
  borderRadius: 'md',
  size: 'sm' as const,
  fontSize: 'sm',
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Flex direction="column" gap={1}>
    <Text fontSize="xs" color="text.secondary">
      {label}
    </Text>
    {children}
  </Flex>
);

const emptyRow = (id: string): MerchandiseDraftRow => ({
  id,
  name: '',
  quantity: '',
  unit: 'pcs',
  category: '',
});

// Stand-in for the rows our AI would extract from an uploaded catalogue/price list.
const fakeExtractedRows: MerchandiseDraftRow[] = [
  { id: 'ex-1', name: 'Raw Shea Butter (Grade A)', quantity: '2500', unit: 'kg', category: 'Oils, fats & extracts' },
  { id: 'ex-2', name: 'Cocoa Beans (Fermented)', quantity: '1800', unit: 'kg', category: 'Coffee, tea, cocoa & spices' },
  { id: 'ex-3', name: 'Cashew Nuts (Raw)', quantity: '600', unit: 'kg', category: 'Nuts & seeds' },
  { id: 'ex-4', name: 'Palm Kernel Oil', quantity: '400', unit: 'liters', category: 'Oils, fats & extracts' },
];

const NewMerchandiseDialog = memo(({ open, onClose, initialRows, title }: Props) => {
  const isEditing = initialRows != null;
  const rowCounter = useRef(0);
  const [rows, setRows] = useState<MerchandiseDraftRow[]>(initialRows ?? [emptyRow('row-0')]);
  const [extraction, setExtraction] = useState<ExtractionState>('idle');
  const [fileName, setFileName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reseed whenever the dialog opens so edit/create start from the right rows.
  useEffect(() => {
    if (open) {
      rowCounter.current = 0;
      setRows(initialRows ?? [emptyRow('row-0')]);
      setExtraction('idle');
      setFileName(null);
    }
  }, [open, initialRows]);

  const handleOpenChange = useCallback(
    (e: { open: boolean }) => {
      if (e.open === false) {
        onClose();
      }
    },
    [onClose],
  );

  const onChangeName = useCallback(
    (id: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = e.target.value;
      setRows((items) => items.map((item) => (item.id === id ? { ...item, name: next } : item)));
    },
    [],
  );

  const onChangeQuantity = useCallback(
    (id: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = e.target.value;
      setRows((items) => items.map((item) => (item.id === id ? { ...item, quantity: next } : item)));
    },
    [],
  );

  const onChangeUnit = useCallback(
    (id: string) => (details: { value: string[] }) => {
      const next = details.value[0] ?? 'pcs';
      setRows((items) => items.map((item) => (item.id === id ? { ...item, unit: next } : item)));
    },
    [],
  );

  const onChangeCategory = useCallback(
    (id: string) => (category: string) => {
      setRows((items) => items.map((item) => (item.id === id ? { ...item, category } : item)));
    },
    [],
  );

  const onAddRow = useCallback(() => {
    setRows((items) => [...items, emptyRow(`row-${++rowCounter.current}`)]);
  }, []);

  const onRemoveRow = useCallback(
    (id: string) => () => {
      setRows((items) => (items.length > 1 ? items.filter((item) => item.id !== id) : items));
    },
    [],
  );

  const onPickFile = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const onFileSelected = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file == null) {
      return;
    }
    setFileName(file.name);
    setExtraction('parsing');
    // UI-only: simulate the AI extraction round-trip, then drop the parsed rows in.
    setTimeout(() => {
      rowCounter.current = fakeExtractedRows.length;
      setRows(fakeExtractedRows);
      setExtraction('done');
    }, 1600);
  }, []);

  // UI-only: no backend yet, so saving just closes the dialog.
  const onSave = useCallback(() => {
    onClose();
  }, [onClose]);

  // Stacked fields per item so the form stays readable at any width. Editing is a
  // single item (no card chrome, no add/remove); adding wraps each item in a card
  // with its own remove control plus an "Add item" button.
  const itemsForm = (
    <Flex direction="column" gap={3}>
      {rows.map((row, index) => {
        const fields = (
          <>
            <Field label="Item">
              <Input
                placeholder="e.g. Raw Shea Butter"
                value={row.name}
                onChange={onChangeName(row.id)}
                {...inputProps}
              />
            </Field>
            <Field label="Category">
              <EntityPicker
                value={row.category !== '' ? row.category : undefined}
                options={categoryOptions}
                placeholder="Select category"
                createLabel="category"
                onSelect={onChangeCategory(row.id)}
              />
            </Field>
            <SimpleGrid columns={2} gap={3}>
              <Field label="Quantity">
                <Input
                  type="number"
                  placeholder="0"
                  value={row.quantity}
                  onChange={onChangeQuantity(row.id)}
                  {...inputProps}
                />
              </Field>
              <Field label="Unit">
                <SelectField
                  collection={unitCollection}
                  placeholder="Unit"
                  value={[row.unit]}
                  onValueChange={onChangeUnit(row.id)}
                />
              </Field>
            </SimpleGrid>
          </>
        );

        if (isEditing) {
          return (
            <Flex key={row.id} direction="column" gap={3}>
              {fields}
            </Flex>
          );
        }

        return (
          <Flex
            key={row.id}
            direction="column"
            gap={3}
            borderWidth="1px"
            borderColor="border.light"
            borderRadius="lg"
            p={4}
          >
            <Flex alignItems="center" justifyContent="space-between">
              <Text fontSize="xs" fontWeight="medium" color="text.secondary">
                Item {index + 1}
              </Text>
              {rows.length > 1 && (
                <IconButton
                  variant="ghost"
                  size="xs"
                  color="text.secondary"
                  aria-label="Remove item"
                  onClick={onRemoveRow(row.id)}
                >
                  <LuX size={14} />
                </IconButton>
              )}
            </Flex>
            {fields}
          </Flex>
        );
      })}
      {!isEditing && (
        <Button variant="ghost" size="xs" borderRadius="md" alignSelf="flex-start" onClick={onAddRow}>
          <LuPlus size={14} />
          Add item
        </Button>
      )}
    </Flex>
  );

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange} size="lg" scrollBehavior="inside">
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content borderRadius="xl" maxW="520px">
            <Dialog.Header px={6} pt={6} pb={0}>
              <Dialog.Title fontSize="lg">{title ?? 'New merchandise'}</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body px={6} py={4}>
              {isEditing ? (
                itemsForm
              ) : (
                <Tabs.Root defaultValue="manual" variant="enclosed" size="sm">
                  <Tabs.List mb={4}>
                    <Tabs.Trigger value="manual">
                      <LuPenLine size={14} />
                      Add manually
                    </Tabs.Trigger>
                    <Tabs.Trigger value="upload">
                      <LuFileUp size={14} />
                      Upload file
                    </Tabs.Trigger>
                  </Tabs.List>
                  <Tabs.Content value="manual" px={0}>
                    {itemsForm}
                  </Tabs.Content>
                  <Tabs.Content value="upload" px={0}>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,.pdf"
                      onChange={onFileSelected}
                      style={{ display: 'none' }}
                    />
                    {extraction === 'idle' && (
                      <Flex
                        direction="column"
                        alignItems="center"
                        justifyContent="center"
                        gap={2}
                        borderWidth="1px"
                        borderStyle="dashed"
                        borderColor="border.light"
                        borderRadius="lg"
                        py={10}
                        px={6}
                        cursor="pointer"
                        _hover={{ bg: 'surface.muted' }}
                        onClick={onPickFile}
                      >
                        <LuFileUp size={28} color="var(--chakra-colors-text-secondary)" />
                        <Text fontSize="sm" fontWeight="medium">
                          Upload a CSV or PDF
                        </Text>
                        <Text fontSize="xs" color="text.secondary" textAlign="center" maxW="320px">
                          Drop in a price list, catalogue, or spreadsheet — our AI reads it and pulls
                          out each item and quantity for you to review.
                        </Text>
                        <Button variant="outline" size="xs" borderRadius="md" mt={1}>
                          Choose file
                        </Button>
                      </Flex>
                    )}
                    {extraction === 'parsing' && (
                      <Flex
                        direction="column"
                        alignItems="center"
                        justifyContent="center"
                        gap={3}
                        borderWidth="1px"
                        borderColor="border.light"
                        borderRadius="lg"
                        py={12}
                        px={6}
                      >
                        <Spinner size="md" color="purple.500" />
                        <Flex alignItems="center" gap={1.5}>
                          <LuSparkles size={15} color="var(--chakra-colors-purple-500)" />
                          <Text fontSize="sm" fontWeight="medium">
                            Extracting items from {fileName}…
                          </Text>
                        </Flex>
                        <Text fontSize="xs" color="text.secondary">
                          This usually takes a few seconds.
                        </Text>
                      </Flex>
                    )}
                    {extraction === 'done' && (
                      <Flex direction="column" gap={3}>
                        <Flex
                          alignItems="center"
                          gap={1.5}
                          bg="purple.50"
                          _dark={{ bg: 'purple.950' }}
                          borderRadius="md"
                          px={3}
                          py={2}
                        >
                          <LuSparkles size={14} color="var(--chakra-colors-purple-500)" />
                          <Text fontSize="xs" color="text.secondary">
                            Pulled {fakeExtractedRows.length} items from{' '}
                            <Text as="span" fontWeight="medium" color="text.primary">
                              {fileName}
                            </Text>
                            . Review and edit before saving.
                          </Text>
                        </Flex>
                        {itemsForm}
                      </Flex>
                    )}
                  </Tabs.Content>
                </Tabs.Root>
              )}
            </Dialog.Body>
            <Dialog.Footer px={6} pb={6} pt={2} gap={3}>
              <Button variant="outline" size="sm" borderRadius="md" onClick={onClose}>
                Cancel
              </Button>
              <Button size="sm" borderRadius="md" onClick={onSave}>
                {isEditing ? 'Update' : 'Save merchandise'}
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
});
NewMerchandiseDialog.displayName = 'NewMerchandiseDialog';

export default NewMerchandiseDialog;
